import { useState, useCallback, useEffect } from 'react';
import { WidgetState } from '../types';

const STORAGE_KEY = 'chatWidget_unreadCount';

export const useChatWidget = () => {
  const [state, setState] = useState<WidgetState>({
    isExpanded: false,
    isSidebarOpen: true,
    isMaximized: false,
    showTooltip: false,
    unreadCount: 0,
  });

  // Load unread count from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const count = parseInt(stored, 10);
      if (!isNaN(count)) {
        setState(prev => ({ ...prev, unreadCount: count }));
      }
    }
  }, []);

  // Save unread count to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, state.unreadCount.toString());
  }, [state.unreadCount]);

  // Show tooltip to first-time visitors after 3s
  useEffect(() => {
    const seen = localStorage.getItem('chatTooltipSeen');
    if (seen) return;
    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, showTooltip: true }));
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const dismissTooltip = useCallback(() => {
    localStorage.setItem('chatTooltipSeen', '1');
    setState(prev => ({ ...prev, showTooltip: false }));
  }, []);

  const openWidget = useCallback(() => {
    localStorage.setItem('chatTooltipSeen', '1');
    setState(prev => ({
      ...prev,
      isExpanded: true,
      showTooltip: false,
      unreadCount: 0,
    }));
  }, []);

  const closeWidget = useCallback(() => {
    setState(prev => ({
      ...prev,
      isExpanded: false,
      isSidebarOpen: false, // Close sidebar when closing widget
    }));
  }, []);

  const toggleWidget = useCallback(() => {
    setState(prev => ({
      ...prev,
      isExpanded: !prev.isExpanded,
      unreadCount: prev.isExpanded ? prev.unreadCount : 0,
      isSidebarOpen: prev.isExpanded ? false : prev.isSidebarOpen,
    }));
  }, []);

  const toggleSidebar = useCallback(() => {
    setState(prev => ({
      ...prev,
      isSidebarOpen: !prev.isSidebarOpen,
    }));
  }, []);

  const toggleMaximize = useCallback(() => {
    setState(prev => ({ ...prev, isMaximized: !prev.isMaximized }));
  }, []);

  const closeSidebar = useCallback(() => {
    setState(prev => ({
      ...prev,
      isSidebarOpen: false,
    }));
  }, []);

  const incrementUnreadCount = useCallback(() => {
    setState(prev => ({
      ...prev,
      unreadCount: prev.unreadCount + 1,
    }));
  }, []);

  return {
    ...state,
    openWidget,
    closeWidget,
    toggleWidget,
    toggleSidebar,
    toggleMaximize,
    closeSidebar,
    dismissTooltip,
    incrementUnreadCount,
  };
};
